import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { OnItemsRenderedType } from '.';

const LISTBOX_PADDING = 8; // px
const DEFAULT_EXPAND_MAX_HEIGHT = 480;

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet && dataSet.hasOwnProperty('group')) {
        return (
            <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        );
    }

    return (
        <Typography component="li" {...dataSet.props} noWrap style={inlineStyle}>
            {dataSet?.props?.children}
        </Typography>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: unknown) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const readExpandListMaxHeight = (wrapperEl: HTMLDivElement | null): number => {
    if (!wrapperEl) {
        return DEFAULT_EXPAND_MAX_HEIGHT;
    }

    const popper = wrapperEl.closest('.MuiAutocomplete-popper') as HTMLElement | null;
    if (popper) {
        const cssVar = getComputedStyle(popper).getPropertyValue('--expand-list-max-height').trim();
        if (cssVar.endsWith('px')) {
            return parseInt(cssVar, 10);
        }
    }

    const combobox = wrapperEl.closest('.MuiAutocomplete-root')?.querySelector('[role="combobox"]');
    if (combobox instanceof HTMLElement) {
        const rect = combobox.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - 16;
        const spaceAbove = rect.top - 16;
        return Math.min(
            Math.floor(window.innerHeight * 0.6),
            Math.max(Math.max(spaceBelow, spaceAbove), 200)
        );
    }

    return DEFAULT_EXPAND_MAX_HEIGHT;
};

interface ExendProps extends React.HTMLAttributes<HTMLElement> {
    onItemsRendered?: OnItemsRenderedType;
    expandList?: boolean;
}

const ListboxComponent = forwardRef<HTMLDivElement, ExendProps>(function ListboxComponent(props, ref) {
    const { children, onItemsRendered, expandList, ...other } = props;
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [expandMaxHeight, setExpandMaxHeight] = useState(DEFAULT_EXPAND_MAX_HEIGHT);
    const itemData: React.ReactChild[] = [];
    (children as React.ReactChild[]).forEach((item: React.ReactChild & { children?: React.ReactChild[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 40 : 48;

    useLayoutEffect(() => {
        if (!expandList) {
            return;
        }
        setExpandMaxHeight(readExpandListMaxHeight(wrapperRef.current));
    }, [expandList, itemCount]);

    const getChildSize = (child: React.ReactChild) => {
        if (child && child.hasOwnProperty('group')) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        const contentHeight = itemData.map(getChildSize).reduce((a, b) => a + b, 0);
        if (expandList) {
            const availableHeight = expandMaxHeight - 2 * LISTBOX_PADDING;
            return Math.min(contentHeight, availableHeight);
        }
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return contentHeight;
    };

    const gridRef = useResetCache(itemCount);

    const setWrapperRef = (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    return (
        <div ref={setWrapperRef}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                    onItemsRendered={onItemsRendered}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

export default ListboxComponent;
